import { useEffect, useRef } from "react";
import logo from "./octal-logo.png";


export default function Home() {
    const rootRef = useRef(null);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const burger = root.querySelector('#burger');
        const mobileMenu = root.querySelector('#mobileMenu');
        const onBurgerClick = () => {
            mobileMenu.classList.toggle('open');
            burger.classList.toggle('open');
        };
        burger.addEventListener('click', onBurgerClick);

        const mobileLinks = root.querySelectorAll('#mobileMenu a');
        const onMobileLinkClick = () => {
            mobileMenu.classList.remove('open');
            burger.classList.remove('open');
        };
        mobileLinks.forEach(a => a.addEventListener('click', onMobileLinkClick));

        const revealEls = root.querySelectorAll('.reveal');
        const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in');
                    io.unobserve(entry.target);
                }
            });
        }, { threshold: 0.15 });
        revealEls.forEach(el => io.observe(el));

        const siteHeader = root.querySelector('#siteHeader');
        const scrollProgress = root.querySelector('#scrollProgress');
        const onScroll = () => {
            if (window.scrollY > 8) siteHeader.classList.add('scrolled');
            else siteHeader.classList.remove('scrolled');
            const h = document.documentElement;
            const pct = (h.scrollTop) / (h.scrollHeight - h.clientHeight) * 100;
            scrollProgress.style.width = pct + '%';
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();

        const navAnchors = root.querySelectorAll('.nav-links a');
        const sections = [...navAnchors].map(a => root.querySelector(a.getAttribute('href')));
        const navIO = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const id = '#' + entry.target.id;
                const link = root.querySelector('.nav-links a[href="' + id + '"]');
                if (!link) return;
                if (entry.isIntersecting) {
                    navAnchors.forEach(a => a.classList.remove('active'));
                    link.classList.add('active');
                }
            });
        }, { rootMargin: '-45% 0px -45% 0px' });
        sections.forEach(s => s && navIO.observe(s));

        const cards = root.querySelectorAll('.card');
        const onCardMove = (e) => {
            const card = e.currentTarget;
            const r = card.getBoundingClientRect();
            card.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
            card.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100) + '%');
        };
        cards.forEach(card => card.addEventListener('mousemove', onCardMove));

        return () => {
            burger.removeEventListener('click', onBurgerClick);
            mobileLinks.forEach(a => a.removeEventListener('click', onMobileLinkClick));
            revealEls.forEach(el => io.unobserve(el));
            window.removeEventListener('scroll', onScroll);
            sections.forEach(s => s && navIO.unobserve(s));
            cards.forEach(card => card.removeEventListener('mousemove', onCardMove));
        };
    }, []);

    return (
        <div ref={rootRef}>
            <div className="scroll-progress" id="scrollProgress"></div>

            <header id="siteHeader">
                <nav>
                    <div className="logo">
                        <a href="/">  <img src={logo} alt="Octal Optimum" /></a>
                    </div>
                    <div className="nav-links" id="navLinks">
                        <a href="#home">Home</a>
                        <a href="#about">About</a>
                        <a href="#services">Capabilities</a>
                        <a href="#process">Process</a>
                        <a href="#industries">Industries</a>
                        <a href="#why">Why Us</a>
                        <a href="#contact">Contact</a>
                    </div>
                    <div className="nav-cta"><a href="#contact" className="btn btn-primary">Let's Build Together</a></div>
                    <button className="burger" id="burger" aria-label="Toggle menu"><span></span><span></span><span></span></button>
                </nav>
            </header>
            <div className="mobile-menu" id="mobileMenu">
                <a href="#home">Home</a>
                <a href="#about">About</a>
                <a href="#services">Capabilities</a>
                <a href="#process">Process</a>
                <a href="#industries">Industries</a>
                <a href="#why">Why Us</a>
                <a href="#contact">Contact</a>
            </div>

            <section className="hero" id="home">
                <svg className="hero-trace" viewBox="0 0 1180 500" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M -20 420 L 220 420 L 260 380 L 520 380 L 560 340 L 900 340 L 940 300 L 1200 300"
                        fill="none" stroke="#00AFEF" strokeWidth="1.4" strokeDasharray="4 6" opacity="0.5" />
                    <path d="M -20 120 L 180 120 L 220 160 L 480 160 L 520 200 L 860 200 L 900 240 L 1200 240"
                        fill="none" stroke="#1E4A6B" strokeWidth="1.4" strokeDasharray="4 6" opacity="0.6" />
                    <circle cx="220" cy="420" r="3.5" fill="#00AFEF" />
                    <circle cx="560" cy="340" r="3.5" fill="#00AFEF" />
                    <circle cx="940" cy="300" r="3.5" fill="#00AFEF" />
                    <circle cx="220" cy="160" r="3" fill="#1E4A6B" />
                    <circle cx="860" cy="200" r="3" fill="#1E4A6B" />
                </svg>
                <div className="wrap hero-inner hero-grid">
                    <div>
                        <div className="eyebrow">ERP · CRM · ED-TECH · CUSTOM ENTERPRISE SYSTEMS</div>
                        <h1>Process. Precision. <em>Progress.</em></h1>
                        <p className="lead">We engineer solutions that redefine how organizations grow, collaborate and deliver value — turning complexity into clarity and potential into progress.</p>
                        <div className="tag-strip">
                            <span>ERP</span><span>CRM</span><span>ED-TECH</span><span>CUSTOM SYSTEMS</span><span>DIGITAL GROWTH</span>
                        </div>
                        <div className="hero-actions">
                            <a href="#contact" className="btn btn-primary">Let's Build Together <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></a>
                            <a href="#services" className="btn btn-ghost">Explore Our Capabilities</a>
                        </div>
                    </div>
                    <div className="hero-panel-wrap reveal">
                        <div className="hero-panel">
                            <div className="panel-bar">
                                <span className="pdot" style={{ background: '#ED3237' }}></span>
                                <span className="pdot" style={{ background: '#F58634' }}></span>
                                <span className="pdot" style={{ background: '#00A859' }}></span>
                                <span className="panel-title">OCTAL · UNIFIED SYSTEM</span>
                            </div>
                            <div className="panel-grid">
                                <div className="panel-tile">
                                    <div className="panel-tile-head"><span>ERP</span><i></i></div>
                                    <div className="bars"><b style={{ height: '40%', animationDelay: '.05s' }}></b><b style={{ height: '70%', animationDelay: '.1s' }}></b><b style={{ height: '55%', animationDelay: '.15s' }}></b><b style={{ height: '85%', animationDelay: '.2s' }}></b><b style={{ height: '60%', animationDelay: '.25s' }}></b></div>
                                </div>
                                <div className="panel-tile">
                                    <div className="panel-tile-head"><span>CRM</span><i></i></div>
                                    <div className="bars"><b style={{ height: '60%', animationDelay: '.1s' }}></b><b style={{ height: '45%', animationDelay: '.15s' }}></b><b style={{ height: '80%', animationDelay: '.2s' }}></b><b style={{ height: '50%', animationDelay: '.25s' }}></b><b style={{ height: '90%', animationDelay: '.3s' }}></b></div>
                                </div>
                                <div className="panel-tile">
                                    <div className="panel-tile-head"><span>ED-TECH</span><i></i></div>
                                    <div className="bars"><b style={{ height: '50%', animationDelay: '.15s' }}></b><b style={{ height: '65%', animationDelay: '.2s' }}></b><b style={{ height: '40%', animationDelay: '.25s' }}></b><b style={{ height: '75%', animationDelay: '.3s' }}></b><b style={{ height: '55%', animationDelay: '.35s' }}></b></div>
                                </div>
                                <div className="panel-tile">
                                    <div className="panel-tile-head"><span>ANALYTICS</span><i></i></div>
                                    <div className="bars"><b style={{ height: '70%', animationDelay: '.2s' }}></b><b style={{ height: '55%', animationDelay: '.25s' }}></b><b style={{ height: '85%', animationDelay: '.3s' }}></b><b style={{ height: '65%', animationDelay: '.35s' }}></b><b style={{ height: '95%', animationDelay: '.4s' }}></b></div>
                                </div>
                            </div>
                            <div className="panel-status"><span className="pulse-dot"></span>All systems synced · real-time</div>
                        </div>
                        <div className="hero-panel-back"></div>
                    </div>
                </div>
            </section>

            <section className="about" id="about">
                <div className="wrap">
                    <div className="about-grid">
                        <div className="about-copy reveal">
                            <div className="eyebrow">Our Purpose</div>
                            <h2>We Build Systems That Empower Growth</h2>
                            <p>At Octal Optimum Technologies, we don't just develop software — we craft digital frameworks that move businesses forward.</p>
                            <p>Our focus is on creating process-driven technology ecosystems that help organizations scale efficiently, make smarter decisions and stay future-ready.</p>
                            <p>From ERP and CRM solutions to Ed-Tech platforms and custom enterprise systems, every product we build is designed to simplify operations, strengthen performance and sustain long-term growth.</p>
                            <div className="highlight">PROCESS-DRIVEN · SCALABLE · PURPOSE-BUILT</div>
                        </div>
                        <div className="schema reveal">
                            <svg viewBox="0 0 340 300" xmlns="http://www.w3.org/2000/svg">
                                <rect x="20" y="20" width="120" height="56" rx="2" fill="none" stroke="#16324F" strokeWidth="1.4" />
                                <text x="80" y="53" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fill="#16324F">YOUR PROCESS</text>
                                <rect x="200" y="20" width="120" height="56" rx="2" fill="none" stroke="#3C59A8" strokeWidth="1.4" />
                                <text x="260" y="53" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fill="#3C59A8">YOUR GOALS</text>
                                <rect x="110" y="130" width="120" height="56" rx="2" fill="#16324F" />
                                <text x="170" y="155" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10.5" fill="#fff">STRUCTURE +</text>
                                <text x="170" y="170" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10.5" fill="#fff">INTELLIGENCE</text>
                                <rect x="60" y="235" width="220" height="50" rx="2" fill="none" stroke="#57626F" strokeWidth="1.2" strokeDasharray="3 4" />
                                <text x="170" y="265" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fill="#57626F">STRATEGIC ADVANTAGE</text>
                                <path d="M80 76 L150 130" stroke="#16324F" strokeWidth="1.2" fill="none" />
                                <path d="M260 76 L190 130" stroke="#3C59A8" strokeWidth="1.2" fill="none" />
                                <path d="M170 186 L170 235" stroke="#57626F" strokeWidth="1.2" fill="none" />
                            </svg>
                        </div>
                    </div>
                </div>
            </section>

            <section className="services" id="services">
                <div className="wrap">
                    <div className="section-head reveal">
                        <div className="eyebrow">What We Build</div>
                        <h2>Systems Designed to Grow With You</h2>
                        <p>We design technology ecosystems that grow with you — from operational backbone to the platforms that connect people.</p>
                    </div>
                    <div className="card-grid">
                        <div className="card reveal">
                            <div className="card-icon"><svg viewBox="0 0 24 24"><path d="M12 3 3 8l9 5 9-5-9-5Z" /><path d="M3 12l9 5 9-5" /><path d="M3 16l9 5 9-5" /></svg></div>
                            <div className="num">ERP · CORE SYSTEMS</div>
                            <h3>ERP Solutions</h3>
                            <p>Unified systems that bring teams, data and processes together — improving visibility, accountability and performance across your enterprise.</p>
                            <div className="cta">Optimize Operations <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                        </div>
                        <div className="card reveal">
                            <div className="card-icon"><svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><circle cx="18" cy="9" r="2.4" /><path d="M15.8 13a4.5 4.5 0 0 1 5.2 4.4" /></svg></div>
                            <div className="num">CRM · CLIENT SYSTEMS</div>
                            <h3>CRM Platforms</h3>
                            <p>Smart, scalable CRMs that help organizations nurture relationships and accelerate sales — making every client interaction count.</p>
                            <div className="cta">Drive Relationships <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                        </div>
                        <div className="card reveal">
                            <div className="card-icon"><svg viewBox="0 0 24 24"><path d="M2 8l10-5 10 5-10 5-10-5Z" /><path d="M6 10.5V16c0 1.3 2.7 3 6 3s6-1.7 6-3v-5.5" /><path d="M22 8v6" /></svg></div>
                            <div className="num">ED-TECH · INSTITUTIONS</div>
                            <h3>Ed-Tech &amp; School ERP</h3>
                            <p>Integrated learning and management platforms that simplify institutional operations and enhance student-teacher engagement.</p>
                            <div className="cta">Empower Learning <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                        </div>
                        <div className="card reveal">
                            <div className="card-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" /></svg></div>
                            <div className="num">CUSTOM · BESPOKE BUILD</div>
                            <h3>Custom Business Software</h3>
                            <p>Tailored digital solutions for enterprises seeking agility, efficiency and technology built precisely around their way of working.</p>
                            <div className="cta">Build Your Solution <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                        </div>
                        <div className="card reveal">
                            <div className="card-icon"><svg viewBox="0 0 24 24"><circle cx="5" cy="6" r="2.3" /><circle cx="19" cy="6" r="2.3" /><circle cx="12" cy="12" r="2.3" /><circle cx="5" cy="18" r="2.3" /><circle cx="19" cy="18" r="2.3" /><path d="M6.8 7.4 10.3 10.6M17.2 7.4 13.7 10.6M10.3 13.4 6.8 16.6M13.7 13.4 17.2 16.6" /></svg></div>
                            <div className="num">COMMUNITY · ENGAGEMENT</div>
                            <h3>Community &amp; Service Platforms</h3>
                            <p>Digital platforms that connect people and enable engagement — from spiritual practice to community services.</p>
                            <div className="cta">Connect. Collaborate. Grow. <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                        </div>
                        <div className="card reveal">
                            <div className="card-icon"><svg viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg></div>
                            <div className="num">GROWTH · VISIBILITY</div>
                            <h3>Digital Growth &amp; Visibility</h3>
                            <p>Strategic digital marketing and analytics support that ensures your technology performs and reaches the right audience.</p>
                            <div className="cta">Amplify Growth <svg viewBox="0 0 16 16"><path d="M3 8h9M8 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="process" id="process">
                <div className="wrap">
                    <div className="section-head reveal">
                        <div className="eyebrow">How We Work</div>
                        <h2>A Process That Builds Progress</h2>
                        <p>Transparent, structured and collaborative — designed to deliver measurable outcomes at every stage.</p>
                    </div>
                    <div className="timeline">
                        <div className="t-step reveal">
                            <div className="timeline-track"><div className="t-node"></div></div>
                            <div className="idx">STAGE 01</div>
                            <h3>Understand &amp; Envision</h3>
                            <p>We listen to your needs, study your workflows and define where technology can create the greatest impact.</p>
                        </div>
                        <div className="t-step reveal">
                            <div className="timeline-track"><div className="t-node"></div></div>
                            <div className="idx">STAGE 02</div>
                            <h3>Design &amp; Develop</h3>
                            <p>We architect clean, modular systems that adapt as your organization grows.</p>
                        </div>
                        <div className="t-step reveal">
                            <div className="timeline-track"><div className="t-node"></div></div>
                            <div className="idx">STAGE 03</div>
                            <h3>Implement &amp; Empower</h3>
                            <p>Seamless deployment with documentation, training and support to ensure full adoption across teams.</p>
                        </div>
                        <div className="t-step reveal">
                            <div className="timeline-track"><div className="t-node"></div></div>
                            <div className="idx">STAGE 04</div>
                            <h3>Evolve &amp; Sustain</h3>
                            <p>Continuous refinement — because your process, like your business, should keep evolving.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="industries" id="industries">
                <div className="wrap">
                    <div className="section-head reveal">
                        <div className="eyebrow">Who We Work With</div>
                        <h2>Industries We Empower</h2>
                        <p>Wherever process matters, we deliver clarity through technology.</p>
                    </div>
                    <div className="chip-row reveal">
                        <div className="chip"><span className="dot" style={{ background: 'var(--gold)' }}></span>Enterprises &amp; SMEs</div>
                        <div className="chip"><span className="dot" style={{ background: 'var(--gold)' }}></span>Education &amp; Institutional Management</div>
                        <div className="chip"><span className="dot" style={{ background: 'var(--teal)' }}></span>Real Estate &amp; Property Management</div>
                        <div className="chip"><span className="dot" style={{ background: 'var(--teal)' }}></span>Tours &amp; Travel</div>

                        <div className="chip"><span className="dot" style={{ background: 'var(--teal)' }}></span>Community &amp; Non-Profit Organizations</div>
                        <div className="chip"><span className="dot" style={{ background: 'var(--gold)' }}></span>Digital Marketing &amp; Service Providers</div>
                    </div>
                </div>
            </section>

            <section className="why" id="why">
                <div className="wrap">
                    <div className="section-head reveal">
                        <div className="eyebrow">The Differentiator</div>
                        <h2>Why Partner with Octal Optimum</h2>
                    </div>
                    <div className="why-grid">
                        <ul className="checklist reveal">
                            <li>
                                <div className="check-mark">✓</div>
                                <div><h4>Process-First Mindset</h4><p>We start with your business logic, not generic frameworks.</p></div>
                            </li>
                            <li>
                                <div className="check-mark">✓</div>
                                <div><h4>Scalable Design</h4><p>Systems built to evolve as you do.</p></div>
                            </li>
                            <li>
                                <div className="check-mark">✓</div>
                                <div><h4>Transparent Delivery</h4><p>Documented, accountable and result-oriented.</p></div>
                            </li>
                            <li>
                                <div className="check-mark">✓</div>
                                <div><h4>Strategic Partnership</h4><p>We think beyond deployment — we plan for growth.</p></div>
                            </li>
                            <li>
                                <div className="check-mark">✓</div>
                                <div><h4>Sustainable Innovation</h4><p>Practical, efficient and built to last.</p></div>
                            </li>
                        </ul>
                        <div className="pull-quote reveal">
                            <p>We don't just build technology — we build capability.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="vision">
                <div className="wrap">
                    <div className="eyebrow">Our Vision</div>
                    <h2>To create digital ecosystems that combine structure with intelligence — helping organizations operate seamlessly, decide confidently and grow continuously.</h2>
                    <p className="sub">We see technology not as a service, but as a strategic partnership that transforms how businesses work, connect and thrive.</p>
                    <div className="stamp">AT OCTAL OPTIMUM, PROCESS IS OUR FOUNDATION AND PROGRESS IS OUR PROMISE.</div>
                </div>
            </section>

            <section className="contact" id="contact">
                <div className="wrap">
                    <div className="contact-inner">
                        <div className="reveal">
                            <div className="eyebrow">Engagement Point</div>
                            <h2>Let's Build Your Next Chapter</h2>
                            <p className="lead">You bring the vision — we bring the process, precision and partnership to make it real.</p>
                            <div className="contact-actions">
                                <a href="mailto:info@octaloptimum.com" className="btn btn-dark">Start a Conversation →</a>
                                <a href="mailto:info@octaloptimum.com?subject=Discovery%20Call" className="btn btn-dark">Schedule a Discovery Call →</a>
                            </div>
                        </div>
                        <div className="contact-card reveal">
                            <div className="row"><div className="row-icon"><svg viewBox="0 0 24 24"><path d="M3 6h18v12H3z" /><path d="m3 7 9 6 9-6" /></svg></div><div><div className="label">Email</div><div><a href="mailto:info@octaloptimum.com">info@octaloptimum.com</a></div></div></div>

                            <div className="row"><div className="row-icon"><svg viewBox="0 0 24 24"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" /></svg></div><div><div className="label">Phone</div><div><a href="tel:+918310205563">+91-83102 05563</a></div></div></div>
                            <div className="row"><div className="row-icon"><svg viewBox="0 0 24 24"><path d="M12 21s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12Z" /><circle cx="12" cy="9" r="2.4" /></svg></div><div><div className="label">Address</div><div>#1278, 1st Floor, 25th Main, 40th Cross Rd, 9th Block, Jayanagar, Bengaluru, Karnataka 560041</div></div></div>
                        </div>
                    </div>
                </div>
            </section>

            <footer>
                <div className="wrap">
                    <div className="footer-top">
                        <div className="footer-brand">
                            <div className="logo" style={{ color: 'var(--white)' }}>
                                <img src={logo} alt="" />
                            </div>
                            <p>We engineer process-driven ERP, CRM, Ed-Tech and custom enterprise systems that help organizations scale efficiently and stay future-ready.</p>
                        </div>
                        <div className="footer-col">
                            <h5>Explore</h5>
                            <a href="#about">About</a>
                            <a href="#services">Capabilities</a>
                            <a href="#process">Process</a>
                            <a href="#industries">Industries</a>
                        </div>
                        <div className="footer-col">
                            <h5>Capabilities</h5>
                            <a href="#services">ERP Solutions</a>
                            <a href="#services">CRM Platforms</a>
                            <a href="#services">Ed-Tech &amp; School ERP</a>
                            <a href="#services">Custom Business Software</a>
                        </div>
                        <div className="footer-col">
                            <h5>Get in Touch</h5>
                            <a href="mailto:info@octaloptimum.com">info@octaloptimum.com</a>
                            <a href="tel:+918310205563">+91-83102 05563</a>
                            <a href="#contact">#1278, 1st Floor, 25th Main, 40th Cross Rd, 9th Block, Jayanagar, Bengaluru, Karnataka 560041</a>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <div>&copy; 2016-{new Date().getFullYear().toString().slice(-2)} Octal Optimum Technologies. All rights reserved.</div>
                        {/* <div className="footer-legal">
                            <a href="#">Privacy Policy</a>
                            <a href="#">Terms of Service</a>
                        </div> */}
                    </div>
                </div>
            </footer>
        </div>
    );
}